import { getSplitsPayment } from 'modules/services/utils/billingUtils';

import { initialValues } from 'common/components/Steps/TrialMembershipStep/initialValues';

import {
  IEditablePackageConfiguration,
  IPackageConfiguration,
  IAddIncludedPackageDTO,
} from 'common/interfaces/membership';

export const transformPackageToDTO = (
  packageItem: IEditablePackageConfiguration,
): IAddIncludedPackageDTO => {
  const {
    billingOption,
    downPayment,
    durationEditableNumber,
    paymentGrace,
    splitting,
    paymentEditableSchedule,
    services,
    freeze,
    dayTimeAvailDtoList,
    feeSection,
    outOfTermBillingOption,
  } = packageItem;

  const { ...rest } = packageItem;
  const { fees = [] } = feeSection || {};

  return {
    ...rest,
    durationNumber: durationEditableNumber?.value,
    billingOption,
    paymentSchedule: paymentEditableSchedule?.value,
    dayTimeAvails: dayTimeAvailDtoList || [],
    hasCustomDayTimeAvailability: !!dayTimeAvailDtoList?.length,
    paymentSplitEnabled: !!splitting && splitting.allow,
    paymentNumber: splitting?.paymentEditableNumber?.value,
    paymentSplits: splitting?.paymentEditableSplits,
    downPaymentEnabled: !!downPayment && downPayment.allow,
    downPaymentNumber: downPayment?.editableNumber?.value,
    downPaymentAmount: downPayment?.editableAmount?.value,
    downPaymentSplits: downPayment?.editableSplits,
    paymentGraceEnabled: !!paymentGrace && paymentGrace.allow,
    paymentGraceNumber: paymentGrace?.editableDurationNumber?.value,
    freezeDaysNumber: freeze?.editableDaysNumber?.value ?? null,
    totalAmount: packageItem.totalAmount.value,

    services: services
      .filter(el => el.included)
      .map(service => ({
        ...service,
        ...(service.package && { package: transformPackageToDTO(service.package) }),
      })),

    fees: fees
      .filter(el => el.included)
      .map(fee => ({
        id: fee.id || '',
        totalAmount: fee.totalAmount?.value,
        chargeAfterDays: fee.chargeAfterDays?.value,
        paymentNumber: fee.split ? fee.paymentsNumber?.value : undefined,
        paymentSplits: fee.split ? fee.editableSplits : undefined,
        fee: fee.fee,
      })),
    outOfTermBillingOption: {
      ...(outOfTermBillingOption as any), // TODO - PRM-3575 need types
      billingOption: outOfTermBillingOption?.id || '',
      freezeDaysNumber: Number(outOfTermBillingOption?.freeze?.editableDaysNumber?.value),
      paymentGraceEnabled: !!outOfTermBillingOption?.paymentGrace?.allow,
      paymentGraceNumber: Number(
        outOfTermBillingOption?.paymentGrace?.editableDurationNumber?.value,
      ),
      paymentNumber: Number(outOfTermBillingOption?.splitting.paymentEditableNumber?.value),
      paymentSchedule: outOfTermBillingOption?.paymentEditableSchedule?.value as any, // TODO - PRM-3575 need types
      paymentSplitEnabled: !!outOfTermBillingOption?.splitting?.allow,
      paymentSplits: outOfTermBillingOption?.splitting?.paymentEditableSplits || [],
      totalAmount: outOfTermBillingOption?.totalAmount?.value,
    },
  };
};

export const transformPackageFromDTO = (
  packageItem: IPackageConfiguration,
): IEditablePackageConfiguration => {
  const billingOption = packageItem.billingOptions?.find(option => option.useAsDefault);

  const {
    id: billingOptionId,
    totalAmount,
    paymentSplitting,
    splitting,
    downPayment,
    paymentGrace,
    paymentEditableSchedule,
    creditCardServiceFee,
    freeze,
    pricePerBilling,
  } = billingOption || {};

  const { billingOptions } = packageItem;

  const outOfTermBillingOption = packageItem.billingOptions?.find(item => item.isOutOfTerm);
  const outOfTermSplitting = outOfTermBillingOption?.splitting;

  return {
    ...(initialValues.packageConfiguration as any), // TODO - PRM-3575 need types
    ...packageItem,

    billingOption: billingOptionId || '',
    outOfTermBillingOption: {
      ...outOfTermBillingOption,
      splitting: {
        ...outOfTermSplitting,
        ...(outOfTermSplitting?.allow
          ? {
              paymentEditableSplits: getSplitsPayment(
                outOfTermSplitting.paymentEditableNumber.value,
                outOfTermBillingOption?.totalAmount?.value,
                false,
              ),
            }
          : {}),
      },
    },
    billingOptions,

    ...(billingOption && {
      totalAmount,
      splitting: {
        ...(splitting || paymentSplitting),
        ...(splitting?.allow
          ? {
              paymentEditableSplits: getSplitsPayment(
                splitting.paymentEditableNumber.value,
                totalAmount?.value,
                false,
              ),
            }
          : {}),
      },
      downPayment,
      paymentGrace,
      paymentEditableSchedule,
      creditCardServiceFee,
      freeze,
      taxedTotalAmount: billingOption.taxedTotalAmount,
      pricePerBilling,
    }),

    feeSection: {
      ...(packageItem.feeSection as any),
      fees: packageItem.feeSection
        ? packageItem.feeSection.fees.map(fee => ({
            ...fee,
            included: true,
            split: !!fee.allowSplitting,
            editableSplits: [
              ...getSplitsPayment(
                fee.paymentsNumber?.value || 0,
                fee.totalAmount?.value || 0,
                false,
              ),
            ],
          }))
        : [],
    },

    ...(packageItem.services?.length && {
      services: packageItem.services.map(serviceItem =>
        serviceItem.package
          ? {
              ...serviceItem,
              included: true,
              package: transformPackageFromDTO(serviceItem.package),
            }
          : { ...serviceItem, included: true },
      ),
    }),
  };
};
